import React, { useState, useEffect, useRef, useMemo } from "react";
import {useIntl, getLocale} from "umi";
import moment from "moment";
import {TAB_NAME} from "../../_config/constants";
import { getTableDefaultSort, getTableDefaultSortParams } from "../../_config/utils";

import SelectShowValueType
, {ShowValueType} from "@/pages/dashboard/pages/overview/components/OverviewTable/components/SelectShowValueType";
import PerformanceMetricsTable from "@/pages/dashboard/components/PerformanceMetricsTable";

import useTableColumns, {RANGE_DATE_KEY_MAP} from "@/pages/dashboard/pages/competitor/_config/useTableColumns";
import { downloadFileWithFileFormat, useNotificationOfDownloadTable } from "@/pages/dashboard/components/DownloadFormatModal";
import { useOverLimitTrigger } from "@/pages/dashboard/components/OverLimitTip";
import {LANGUAGE_MAP, timeType} from "@/pages/dashboard/_config/constant";
import { getDynamicLocalCnEnText } from "@/pages/dashboard/utils/utils";
import {useAccumulateOptionsByFilterValue} from "@/pages/dashboard/components/DimensionAccumulate/hooks";
import DimensionAccumulateRow from "@/pages/dashboard/components/DimensionAccumulateRow";
import { competitorRequest } from "../../_config/competitorRequest";
import { getTableDate } from "../../../realtime/realtimeUsers/component/DataDownloadTableCo/theads";

function KeyMetricsTable(props: any) {
  const {
    gameCode,
    dataFrame = '',
    explainInfo,
    filterLoading, 
    filterValue, 
    competitorReqType,
    isCasual = false,
  } = props;
  const local = getLocale();
  const tableTitle = useIntl().formatMessage({id: "competitor_key_metrics_trend_title"});
  const { setNoticeStatus } = useNotificationOfDownloadTable(tableTitle);
  const [loading, setLoading] = useState(true);
  const [isError, setIsError] = useState<boolean>(false);
  // 分页
  const [paging, setPaging] = useState({pageSize: 10, pageIndex: 1});
  const [selectType, setSelectType] = useState<ShowValueType>("value_only");

  const getEntrySort = (showType: ShowValueType = selectType) =>
    getTableDefaultSort(dataFrame, gameCode, showType);
  const getEntrySortParams = () => getTableDefaultSortParams(dataFrame, gameCode);
  const getSortFallback = () => ({ by: 'date', order: 'desc' as const });

  const [sort, setSort] = useState(() => getTableDefaultSort(dataFrame, gameCode, 'value_only'));
  const [tableData, setTableData] = useState(null);
  const [total, setTotal] = useState(0);
  const [frontLogicalFlag, setFrontLogicalFlag] = useState<string | undefined>(undefined);
  const { overLimitBarTriggerKey, handleResponse: handleOverLimitResponse } = useOverLimitTrigger();

  const options = useAccumulateOptionsByFilterValue(filterValue, true);
  const [accumulated, setAccumulated] = useState<string[]>(['date']);
  const accumulateOldValueRef = useRef(['date']);

  useEffect(() => {
    if (filterLoading) return;
    if (filterValue?.filterValue?.competitor?.length > 0) {
      const entrySortParams = getEntrySortParams();
      setSort(getEntrySort());
      setPaging({pageSize: 10, pageIndex: 1});
      const params = getParams({
        page: 1,
        page_size: 10,
        sorted_by: entrySortParams.sorted_by,
        desc: entrySortParams.desc,
      });
      getKeyMetricsTableData(params);
    }
  }, [filterLoading, filterValue]);

  const getParams = (resetParams = {}) => {
    const { filterValue: formValue, isLaunchTime, isEarliestData } = filterValue || {};
    const {rangeTime, rangeDayMonth, region, system, source, competitor} = formValue;
    const timeType = RANGE_DATE_KEY_MAP[formValue?.rangeTime?.type || "month"];
    const {type: launchTimeType, value: launchTime} = rangeDayMonth || {};
    // switchDatePicker 为 false 表示当前使用 rangeTime，否则使用 dayMonth
    const accumulate = accumulateOldValueRef.current;
    const accumulation = {
      by_zone: accumulate?.includes('zone'),
      by_region: accumulate?.includes('region'),
      by_country: accumulate?.includes('country'),
      by_channel: accumulate?.includes('channel'),
      by_system: accumulate?.includes('system'),
      by_language: accumulate?.includes('language'),
    };
    const {by, order} = sort?.[0] || getSortFallback();
    return {
      accumulation,
      period: TAB_NAME[(isLaunchTime || isEarliestData) ? launchTimeType : timeType],
      game_code: gameCode,
      start_time: (isLaunchTime || isEarliestData) ? launchTime[0] : rangeTime?.value[0],
      end_time: (isLaunchTime || isEarliestData) ? launchTime[1] : rangeTime?.value[1],
      is_launch_time: isEarliestData ? false : isLaunchTime,
      is_earliest_time: isEarliestData,
      filters: {
        market_list: region.markets,
        os_list: system,
        competitor_list: competitor,
        source
      },
      // 分页
      page: paging.pageIndex,
      page_size: paging.pageSize,
      // 排序
      sorted_by: by?.replace(selectType, ''),
      desc: order === 'desc',
      ...resetParams
    };
  };

  const getCopyData = useMemo(() => {
    const tData: any = [];
    const tCopyData: any = [];
    if (!tableData) return {tData,
      tCopyData};
    const params = getParams();
    tableData?.metrics_list?.forEach(item => {
      const temp = {};
      const copyTemp ={};
      temp.date = (params.is_launch_time || params.is_earliest_time) ? item.date : getTableDate(item.date, params.period);
      temp.country = item.country;
      temp.region = item.region;
      temp.system = item.system;
      temp.game_name = item.game_name;
      temp.release_time = item.release_time;

      copyTemp.date = (params.is_launch_time || params.is_earliest_time) ? item.date : getTableDate(item.date, params.period);
      copyTemp.country_name = getDynamicLocalCnEnText('country', item.country, gameCode, getLocale());
      copyTemp.region_name = getDynamicLocalCnEnText('region', item.region, gameCode, getLocale());
      copyTemp.system_name = getDynamicLocalCnEnText('system', item.system, gameCode, getLocale());
      copyTemp.game_name = item.game_name;
      copyTemp.release_time = item.release_time;

      Object.keys(item.metrics || {}).forEach(k => {
        temp[k] = item.metrics[k];
        copyTemp[k] = item.metrics[k];
      });
      tData.push(temp);
      tCopyData.push(copyTemp);
    });
    return {
      tData,
      tCopyData
    };
  }, [tableData, getLocale()]);

  const getKeyMetricsTableData = (params) => {
    setLoading(true);
    setIsError(false);

    competitorRequest.getKeyMetricsPerformance[competitorReqType](params, LANGUAGE_MAP[local]).then((res) => {
      // 处理表格基本数据
      setTableData(res);
      setTotal(res?.row_count);
      setFrontLogicalFlag(res?.front_logical_flag);
      handleOverLimitResponse(res?.front_logical_flag);
    }).catch(_e => {
      setIsError(true);
    }).finally(() => setLoading(false));

  };
  const [columns, setColumns, buildDownloadMetricsParams] = useTableColumns(gameCode, explainInfo, filterValue, accumulateOldValueRef.current, selectType,competitorReqType, isCasual);

  // 下载
  function handleDownload() {
    const downloadPromise = downPromise?.();
    if (downloadPromise) { // 有返回Promise对象时由内部处理下载过程
      // 展示正在下载中通知
      setNoticeStatus("loading");
      // 发起请求下载
      (async () => {
        try {
          const res = await downloadPromise;
          const fileName = `${getLocale() === 'zh-CN' ? tableTitle : tableTitle.replace(/\u0020/g, '_')} ${moment().format('YYYY_MM_DD HH_mm_ss')}`;
          await downloadFileWithFileFormat(res, fileName, {
            onDownloadSuccess() {
              setNoticeStatus("success");
            },
            onDownloadError() {
              setNoticeStatus("error");
            }
          });
        } catch (e) {
          setNoticeStatus("error");
        }
      })();
    }
  }
  function downPromise () {
    const params = getParams();
    const downloadMetricsParams = buildDownloadMetricsParams(accumulateOldValueRef.current);
    return competitorRequest.getKeyMetricsPerformanceDownload[competitorReqType]({
      ...params,
      ...downloadMetricsParams,
    }, LANGUAGE_MAP[local]);
  }

  return (
    <PerformanceMetricsTable
      isShowProgressBar
      timeType={timeType[filterValue?.filterValue?.rangeTime?.type]}
      isHighlightSatSunDay
      title={tableTitle}
      columns={columns}
      records={getCopyData.tData}
      copyConfig={
        {
          copyData: getCopyData.tCopyData
        }
      }
      selectType={selectType}
      sorting={{
        defaultSorts: sort,
        onSortChange(sorts) {
          // Default sorting 菜单：恢复 date 排序（无表头高亮）
          if (!sorts?.length) {
            setSort([]);
            const params = getParams({ sorted_by: 'date', desc: true });
            getKeyMetricsTableData(params);
            return;
          }
          setSort(sorts);
          const { by, order } = sorts[0];
          const params = getParams({
            sorted_by: by?.replace(selectType, ''),
            desc: order === 'desc',
          });
          getKeyMetricsTableData(params);
        }
      }}
      paging={{
        pageIndex: paging.pageIndex,
        pageSize: paging.pageSize,
        recordCount: total,
        pageSizeOptions: [10, 20, 30, 40, 50],
        onPagingChange(query) {
          // 切换分页
          setPaging(query);
          // 发起请求
          const params = getParams({page: query.pageIndex, page_size: query.pageSize,});
          getKeyMetricsTableData(params);
        }
      }}
      topTip={{
        left: (<div className="overall-conext-bottom">
          <DimensionAccumulateRow
            options={options}
            value={accumulated}
            onChange={(value)=>{
              setAccumulated(value);
              if (accumulateOldValueRef?.current?.toString() !== value?.toString()) {
                accumulateOldValueRef.current = value;
								
                setPaging({pageIndex: 1, pageSize: paging.pageSize});
                const entrySortParams = getEntrySortParams();
                setSort(getEntrySort());
                const params = getParams({
                  page: 1,
                  page_size: paging.pageSize,
                  sorted_by: entrySortParams.sorted_by,
                  desc: entrySortParams.desc,
                });
                getKeyMetricsTableData(params);
              }
            }}
          />
          <SelectShowValueType
            timeType={(filterValue?.isLaunchTime || filterValue?.isEarliestData) ? filterValue?.filterValue?.rangeDayMonth?.type : RANGE_DATE_KEY_MAP[filterValue?.filterValue?.rangeTime?.type || "month"]}
            value={selectType}
            onChange={(value) => {
              setSelectType(value);
              if (sort?.[0]) {
                const sortedBy = sort[0].by.replace(selectType, '');
                if (sortedBy && sortedBy !== 'date') {
                  setSort([{ by: `${sortedBy}${value}`, order: sort[0].order }]);
                }
              }
            }}
            isShowDod
          />
        </div>),
        // right: (<MetricsSettings
        //         onConfirm={({metricValues, statisticsType})=> {
        //           setShowStatistic(statisticsType);
        //           setShowMetrics(metricValues)
        //         }}
        //         disabledOptions={[{value: "date", text: useIntl().formatMessage({id: "common_date"})}]}
        //         metricOptions={metricOptions}
        // />)
      }}
      onResizeEnd={(columns) => setColumns(columns)}
      loading={filterLoading || loading}
      isError={isError}
      onDownload={handleDownload}
      frontLogicalFlag={frontLogicalFlag}
      overLimitBarTriggerKey={overLimitBarTriggerKey}
    />);
}

export default KeyMetricsTable;
